import currency from 'currency.js';
import {useUserStore} from '~/store/user';
import type {Pinia} from 'pinia';
import type {FuseResultMatch} from 'fuse.js';
import type {Dayjs} from 'dayjs';
import {useI18n} from 'vue-i18n';
import {CurrencyList} from '~/components/global/utils/CurrencyUtil';
import type {Dictionary} from "lodash";

export default defineNuxtPlugin(({vueApp, $pinia}) => {

  const {$dayjs} = useNuxtApp();

  const userStore = useUserStore($pinia as Pinia);

  const dateFormat = (date: string | Date | undefined | Dayjs, format: string = 'DD MMM, YYYY') => {
    if (!date) return '';
    return $dayjs(date).format(format);
  };
  const timeBetween = (start: string | Date | undefined | Dayjs | null, end: string | Date | undefined | Dayjs | null) => {
    // Uses dayjs to determine how much time has passed between two dates, this should be displayed in a human-readable format like "4 minutes"
    /*if (!start || !end) return '';
    return $dayjs(end).from(start, removeAgo);*/

    if (!start || !end) return '';

    const startDate = $dayjs(start);
    const endDate = $dayjs(end);

    const diffInMilliseconds = endDate.diff(startDate);
    const diffDuration = $dayjs.duration(diffInMilliseconds);

    const {t} = useI18n();

    const units = [
      {value: diffDuration.years(), key: 'years'},
      {value: diffDuration.months(), key: 'months'},
      {value: diffDuration.days(), key: 'days'},
      {value: diffDuration.hours(), key: 'hours'},
      {value: diffDuration.minutes(), key: 'minutes'},
      {value: diffDuration.seconds(), key: 'seconds'}
    ];

    const nonZeroUnits = units.filter(unit => unit.value > 0);

    if (nonZeroUnits.length === 0) {
      return t('time.seconds', {count: 0});
    }

    const formattedUnits = nonZeroUnits
      .slice(0, 2)
      .map(unit => t(`time.${unit.key}`, {count: unit.value}));

    return formattedUnits.join(' ');
  };
  const moneyFormat = (
    value: number | null | undefined,
    symbol: string = 'DKK',
    settings: {
      symbol: string;
      separator: string;
      decimal: string;
      pattern: string;
      negativePattern: string;
      precision: number;
    } = {
      symbol: 'DKK',
      separator: '.',
      decimal: ',',
      pattern: '# !',
      negativePattern: '-# !',
      precision: 2
    }
  ) => {
    if (Number.isNaN(value)) return '';

    const language = userStore.userLanguage || 'en';

    const localeSettings: {
      [key: string]: { separator: string; decimal: string; pattern: string, negativePattern: string }
    } = {
      en: {separator: ',', decimal: '.', pattern: '! #', negativePattern: '-# !'},
      da: {separator: '.', decimal: ',', pattern: '! #', negativePattern: '-# !'}
      // Add more languages here
    };

    const locale = localeSettings[language] || localeSettings['en'];

    const formatSettings = {
      ...settings,
      symbol: symbol,
      separator: locale.separator,
      decimal: locale.decimal,
      pattern: locale.pattern,
      negativePattern: locale.negativePattern
    };

    // Use currency.js to format the value
    if (value === undefined || value === null) return '';

    return currency(value, {
      symbol: formatSettings.symbol,
      separator: formatSettings.separator,
      decimal: formatSettings.decimal,
      precision: formatSettings.precision,
      pattern: formatSettings.pattern,
      negativePattern: formatSettings.negativePattern
    }).format(); // Default fallback
  };

  const numberFormat = (value: number) => {
    let formatter;

    const language = userStore.userLanguage || 'en';

    if (language === 'da') {
      formatter = Intl.NumberFormat('da-DK', {
        maximumFractionDigits: 10
      });
    } else {
      formatter = Intl.NumberFormat('en-US', {
        maximumFractionDigits: 10
      });
    }
    return formatter.format(value);
  };
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp(escapedSearchTerm, 'gi'), match => `<span class="text-paper-blue">${match}</span>`);
  };
  const highlightFuseIndices = (word: string, indices: ReadonlyArray<FuseResultMatch> | undefined, key: string): string => {
    if (!indices || indices.length === 0) return word;

    const indicesToHighlight = indices.find(
      item => item.key === key && word === item.value
    );

    // Check if there are indices to highlight
    if (!indicesToHighlight || indicesToHighlight.indices.length === 0) return word;

    // Split the word into characters for modification
    const characters = word.split('');

    // Loop through the indices and wrap characters in highlight tags
    indicesToHighlight.indices.forEach(([start, end]) => {
      // Ensure indices are within bounds
      if (start >= 0 && end < characters.length) {
        characters[start] = '<span class="text-paper-blue">' + characters[start];
        characters[end] = characters[end] + '</span>';
      }
    });

    return characters.join('');
  };
  const formatPhoneNumber = (phoneNumber: string | null | undefined): string | null => {
    if (!phoneNumber || phoneNumber === '') return null;

    // Use the getCountryCode method to extract the country code
    const countryCode = getCountryCode(phoneNumber);
    // Then remove the country code from the phone number
    const localNumber = phoneNumber.replace(countryCode, '');

    // Format the local number into pairs
    const formattedLocalNumber = localNumber.replace(/(\d{2})(?=\d)/g, '$1 ');

    return `${countryCode} ${formattedLocalNumber}`;
  };
  const getCountryCode = (phoneNumber: string | null | undefined): string => {
    // telCountryCode could come from the users phone number, so we need to extract the country code
    if (!phoneNumber) return '';

    // Extract and sort dial codes from CurrencyList by length (descending)
    const sortedDialCodes = CurrencyList
      .map(currency => currency.dial_code)
      .sort((a, b) => b.length - a.length);

    // Find the first dial code that matches the start of the phone number
    const dialCode = sortedDialCodes.find(code => phoneNumber.startsWith(code));

    return dialCode || '';

  };
  const formatDisplayName = (fullName: string): string => {
    // Split the username by space
    const userNameParts = fullName.trim().split(' ');

    // If the username contains more than one part (i.e., first name and last name)
    if (userNameParts.length > 1) {
      const firstName = userNameParts[0]; // First name
      const lastNameInitial = userNameParts[userNameParts.length - 1][0]; // Initial of the last name
      return `${firstName} ${lastNameInitial}.`;
    }

    // If there is only one part, return it as is (e.g., just a first name)
    return fullName;
  };

  const any = <T>(list: T[] | Record<string, T[]> | Dictionary<T[]> | null | undefined): boolean => {
    if (list == null) return false;

    // Handle arrays
    if (Array.isArray(list)) {
      return list.length > 0;
    }

    // Handle objects/dictionaries
    if (typeof list === 'object') {
      // Check if it has any keys
      const keys = Object.keys(list);
      if (keys.length === 0) return false;

      // Optionally, check if any of the values are non-empty arrays
      for (const key of keys) {
        const value = (list as Record<string, T[]>)[key];
        if (Array.isArray(value) && value.length > 0) {
          return true;
        }
      }
      return false;
    }

    return false;
  };

  vueApp.config.globalProperties.$filters = {
    dateFormat: dateFormat,
    moneyFormat: moneyFormat,
    numberFormat: numberFormat,
    highlightSearchTerm: highlightSearchTerm,
    highlightFuseIndices: highlightFuseIndices
  };

  return {
    provide: {
      dateFormat,
      timeBetween,
      moneyFormat,
      numberFormat,
      highlightSearchTerm,
      highlightFuseIndices,
      formatPhoneNumber,
      getCountryCode,
      formatDisplayName,
      any
    }
  };
});